import { useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { useAuth } from '../context/AuthContext';

export const useMCPMeals = () => {
  const { user } = useAuth();
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Call MCP server
  const callMCPServer = async (command, params = []) => {
    if (!user?.uid) {
      throw new Error('User must be authenticated to use MCP commands');
    }
    
    try {
      const requestBody = {
        command: `${command} ${params.join(' ')}`.trim(),
        userId: user.uid
      };
      
      console.log('callMCPServer: Making request to:', API_ENDPOINTS.MCP);
      console.log('callMCPServer: Request body:', requestBody);
      
      const response = await fetch(API_ENDPOINTS.MCP, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      console.log('callMCPServer: Response status:', response.status);
      console.log('callMCPServer: Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('callMCPServer: Error response:', errorText);
        throw new Error(`MCP Server error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('callMCPServer: Response data:', data);
      return data;
    } catch (error) {
      console.error('callMCPServer: Caught error:', error);
      throw error;
    }
  };

  // Load meals from MCP server
  const loadMeals = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await callMCPServer('list_meals');
      if (result.status === 'success') {
        setMeals(result.meals || []);
      } else {
        throw new Error(result.message || 'Failed to load meals');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Add meal via MCP server
  const addMeal = async (mealData) => {
    if (!user) return null;
    
    try {
      console.log('useMCPMeals: Adding meal with data:', mealData);
      console.log('useMCPMeals: JSON stringified data:', JSON.stringify(mealData));
      
      // Pass the full meal data as JSON string
      const result = await callMCPServer('add_meal', [JSON.stringify(mealData)]);
      
      console.log('useMCPMeals: MCP server response:', result);
      
      if (result.status === 'success') {
        await loadMeals();
        return result.meal;
      } else {
        throw new Error(result.message || 'Failed to add meal');
      }
    } catch (err) {
      console.error('useMCPMeals: Error adding meal:', err);
      setError(err.message);
      throw err;
    }
  };

  // Update meal via MCP server
  const updateMeal = async (id, updates) => {
    if (!user) return null;
    
    try {
      // Convert updates to string format that the Firebase Function can parse
      const updateString = Object.entries(updates)
        .map(([key, value]) => {
          // Handle different data types properly
          if (typeof value === 'string' && value.includes(' ')) {
            // Quote strings with spaces
            return `${key}:"${value}"`;
          } else if (typeof value === 'object') {
            // Stringify objects
            return `${key}:${JSON.stringify(value)}`;
          } else {
            return `${key}:${value}`;
          }
        })
        .join(' ');
      
      console.log('useMCPMeals: Updating meal with:', { id, updates, updateString });
      
      const result = await callMCPServer('update_meal', [id, updateString]);
      
      if (result.status === 'success') {
        await loadMeals();
        return result.meal;
      } else {
        throw new Error(result.message || 'Failed to update meal');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error updating meal:', err);
      throw err;
    }
  };

  // Delete meal via MCP server
  const deleteMeal = async (id) => {
    if (!user) return;
    
    
    try {
      const result = await callMCPServer('delete_meal', [id]);
      
      if (result.status === 'success') {
        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
        await loadMeals();
      } else {
        throw new Error(result.message || 'Failed to delete meal');
      }
    } catch (err) {
      console.error('useMCPMeals: Delete error:', err);
      setError(err.message);
      throw err;
    }
  };

  // Load meals when user changes
  useEffect(() => {
    if (user) {
      loadMeals();
    } else {
      setMeals([]);
    }
  }, [user, loadMeals]);

  return {
    meals,
    loading,
    error,
    addMeal,
    updateMeal,
    deleteMeal,
    loadMeals,
    setError
  };
};
